"use client";
import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoEyeSharp, IoEyeOffSharp } from "react-icons/io5";
import { FaCheck, FaCalendarAlt, FaHistory, FaEnvelope } from "react-icons/fa";
import { AppDispatch } from "../../../feature/store/store";
import { userRegisterApi } from "../../../feature/reducers/userSlice";
import { NotificationService } from "../../../service/NotificationService";

// Buchstaben inkl. Umlaute/Akzente, Leerzeichen, Bindestrich, Apostroph
// (z. B. "Müller", "Meyer-Schmidt", "de Vries", "O'Neill")
const NAME_PATTERN = /^[\p{L}\s'-]+$/u;

const PASSWORD_RULES = [
  { label: "Mindestens 8 Zeichen", test: (v: string) => v.length >= 8 },
  { label: "Ein Großbuchstabe", test: (v: string) => /[A-Z]/.test(v) },
  { label: "Ein Kleinbuchstabe", test: (v: string) => /[a-z]/.test(v) },
  { label: "Eine Zahl", test: (v: string) => /\d/.test(v) },
];

const formSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required("Vorname ist erforderlich")
    .matches(NAME_PATTERN, "Nur Buchstaben, Leerzeichen und Bindestriche"),
  lastName: Yup.string()
    .trim()
    .required("Nachname ist erforderlich")
    .matches(NAME_PATTERN, "Nur Buchstaben, Leerzeichen und Bindestriche"),
  email: Yup.string()
    .trim()
    .email("Ungültige E-Mail-Adresse")
    .required("E-Mail ist erforderlich"),
  password: Yup.string()
    .required("Passwort ist erforderlich")
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/,
      "Das Passwort erfüllt noch nicht alle Regeln.",
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwörter stimmen nicht überein")
    .required("Bitte Passwort wiederholen"),
});

const inputClass =
  "h-[52px] w-full rounded-[10px] border bg-ao-bg px-4 text-[15px] text-ao-fg placeholder:text-ao-muted transition-colors focus:border-ao-accent focus:outline-none";
const labelClass = "text-[13px] font-semibold text-ao-muted";

const BENEFITS = [
  { icon: <FaCalendarAlt />, text: "Werkstatttermine online buchen" },
  { icon: <FaHistory />, text: "Alle Ihre Termine auf einen Blick" },
  { icon: <FaEnvelope />, text: "Bestätigung direkt per E-Mail" },
];

const Register = () => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: formSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(
          userRegisterApi({
            ...values,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
          }),
        ).unwrap();
        NotificationService.success(
          "Konto erstellt! Sie können sich jetzt anmelden.",
        );
        router.push("/login");
      } catch (error: any) {
        // userRegisterApi liefert die Meldung des Servers als Text
        // (z. B. "Diese E-Mail-Adresse wird bereits verwendet.")
        NotificationService.error(
          typeof error === "string"
            ? error
            : error?.message || "Registrierung fehlgeschlagen.",
        );
      }
    },
  });

  const fieldError = (name: keyof typeof formik.values) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  const renderInput = (
    name: keyof typeof formik.values,
    label: string,
    props: React.InputHTMLAttributes<HTMLInputElement>,
  ) => {
    const error = fieldError(name);
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={name} className={labelClass}>
          {label}
        </label>
        <input
          id={name}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          aria-invalid={!!error}
          className={`${inputClass} ${error ? "border-red-500" : "border-ao-line-strong"}`}
          {...props}
        />
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  };

  const passwordError = fieldError("password");
  const confirmError = fieldError("confirmPassword");

  return (
    <section className="bg-ao-bg text-ao-fg">
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1440px] lg:grid-cols-2">
        {/* Links (nur Laptop): Vorteile */}
        <div className="hidden flex-col justify-center gap-10 px-20 py-16 lg:flex">
          <div className="flex flex-col gap-5">
            <span className="text-[13px] font-semibold tracking-[0.18em] text-ao-accent-fg">
              NEUES KONTO
            </span>
            <h1 className="font-display text-7xl font-extrabold uppercase leading-[0.92]">
              Einmal
              <br />
              <span className="text-ao-accent-fg">registrieren.</span>
            </h1>
            <p className="max-w-md text-lg leading-relaxed text-ao-muted">
              Mit Ihrem Konto buchen Sie Termine schneller und sehen alle
              Buchungen an einem Ort.
            </p>
          </div>
          <ul className="flex flex-col gap-4">
            {BENEFITS.map((b) => (
              <li
                key={b.text}
                className="flex items-center gap-4 text-[17px] font-semibold"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ao-surface-2 text-ao-accent-fg">
                  {b.icon}
                </span>
                {b.text}
              </li>
            ))}
          </ul>
        </div>

        {/* Rechts: Formular */}
        <div className="flex items-center justify-center px-5 py-12 lg:px-20">
          <div className="flex w-full max-w-md flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-5xl font-extrabold uppercase leading-none">
                Registrieren
              </h2>
              <p className="text-[15px] text-ao-muted">
                Schon ein Konto?{" "}
                <Link
                  href="/login"
                  className="font-bold text-ao-accent-fg hover:underline"
                >
                  Jetzt anmelden
                </Link>
              </p>
            </div>

            <form
              onSubmit={formik.handleSubmit}
              noValidate
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {renderInput("firstName", "Vorname", {
                  type: "text",
                  autoComplete: "given-name",
                  placeholder: "Max",
                })}
                {renderInput("lastName", "Nachname", {
                  type: "text",
                  autoComplete: "family-name",
                  placeholder: "Mustermann",
                })}
              </div>

              {renderInput("email", "E-Mail", {
                type: "email",
                autoComplete: "email",
                placeholder: "ihre@email.de",
              })}

              {/* Passwort mit Regeln zum Abhaken */}
              <div className="flex flex-col gap-2">
                <label htmlFor="password" className={labelClass}>
                  Passwort
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    autoComplete="new-password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    aria-invalid={!!passwordError}
                    aria-describedby="password-rules"
                    className={`${inputClass} pr-12 ${passwordError ? "border-red-500" : "border-ao-line-strong"}`}
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                    }
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-ao-muted hover:text-ao-fg"
                  >
                    {showPassword ? (
                      <IoEyeOffSharp className="text-lg" />
                    ) : (
                      <IoEyeSharp className="text-lg" />
                    )}
                  </button>
                </div>
                <ul
                  id="password-rules"
                  className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-1"
                >
                  {PASSWORD_RULES.map((rule) => {
                    const ok = rule.test(formik.values.password);
                    return (
                      <li
                        key={rule.label}
                        className={`flex items-center gap-2 text-[13px] ${ok ? "text-ao-fg" : "text-ao-muted"}`}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] ${
                            ok
                              ? "bg-ao-accent text-ao-on-accent"
                              : "border border-ao-line-strong"
                          }`}
                        >
                          {ok && <FaCheck />}
                        </span>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="confirmPassword" className={labelClass}>
                  Passwort wiederholen
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  aria-invalid={!!confirmError}
                  className={`${inputClass} ${confirmError ? "border-red-500" : "border-ao-line-strong"}`}
                />
                {confirmError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {confirmError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="mt-2 flex h-[54px] items-center justify-center rounded-xl bg-ao-accent text-base font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {formik.isSubmitting
                  ? "Konto wird erstellt…"
                  : "Konto erstellen"}
              </button>

              <p className="text-center text-[13px] leading-relaxed text-ao-muted">
                Informationen zur Verarbeitung Ihrer Daten finden Sie in unserer{" "}
                <Link
                  href="/datenschutz"
                  className="font-semibold text-ao-fg underline"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </form>

            <Link
              href="/"
              className="text-center text-sm font-semibold text-ao-muted hover:text-ao-fg"
            >
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Register;
